/**
 * Cross-platform clipboard utility with iOS Safari fallback
 */

export interface ClipboardResult {
  success: boolean;
  error?: string;
}

/**
 * Fallback copy method using deprecated execCommand
 * Works better on iOS Safari in some contexts
 */
function tryFallbackCopy(text: string): ClipboardResult {
  try {
    // Create temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Style to make it invisible but still selectable
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    textArea.style.opacity = '0';
    textArea.setAttribute('readonly', '');

    document.body.appendChild(textArea);

    // Select and copy
    textArea.select();
    textArea.setSelectionRange(0, text.length);

    const success = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (success) {
      return { success: true };
    }
    return {
      success: false,
      error: 'Copy command failed',
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Copies text to clipboard with iOS Safari compatibility
 * Uses modern Clipboard API when available, falls back to legacy methods
 */
export async function copyToClipboard(text: string): Promise<ClipboardResult> {
  // Try modern Clipboard API first (preferred method)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } catch {
      // If modern API fails, try fallback
      return tryFallbackCopy(text);
    }
  }

  // Use fallback for older browsers or insecure contexts
  return tryFallbackCopy(text);
}

/**
 * Checks if clipboard functionality is available
 */
export function isClipboardSupported(): boolean {
  return !!(navigator.clipboard || document.execCommand);
}
